import { useContext, useState } from "react"

import { 
  EllipsisDropdown, 
  EmailPreviewModal,
  EmptyPlaceholder,
  EventMenu,
  EventPageHeader, 
  EventViewsTracker,
  LoadingPlaceholder, 
  NotificationsWizard, 
  Page,
} from "@components"

import { 
  ContextualSidebarContext, 
  ModalContext, 
  NotificationContext, 
  UserContext 
} from "@context"

import { 
  GET_PREVIEW_BOOKING_CONFIRMATION_EMAIL, 
  GET_PREVIEW_INVITATION_EMAIL,
  GET_PREVIEW_NOTIFICATION_EMAIL,
  GET_NOTIFICATIONS, 
  GET_NOTIFICATION_META_DATA, 
  GET_SES_EMAIL_BATCHES,
  UPDATE_NOTIFICATION_MUTATION 
} from "@graphql"

import { 
  getEventDateRange, 
  getGraphQLErrorMessage, 
  hasExpired,
  mySqlToLocal 
} from "@helpers"

import { useSetCurrentEvent } from "@hooks"
import { useLazyQuery, useMutation, useQuery } from "@apollo/client"

import { 
  Button, 
  Card, 
  CardBody, 
  CardHeader, 
  Chip, 
  Spinner, 
  Table, 
  TableBody, 
  TableCell, 
  TableColumn, 
  TableHeader, 
  TableRow 
} from "@heroui/react"

import {
  BellAlertIcon,
  BellIcon,  
  BellSlashIcon,
  BellSnoozeIcon,
  CircleStackIcon,
  EnvelopeIcon, 
} from "@heroicons/react/24/outline"

import { truncate } from "lodash"
import { useParams } from "react-router-dom"
import CheckList from "./CheckList"
import NotificationDetails from "./NotificationDetails"
import NotificationDeliveryStats from "./NotificationDeliveryStats"

const notificationTypeColors = {
  CUSTOM_EMAIL: { bgColor: "bg-purple-500", iconColor: "text-purple-500" },
  BOOKING_CANCELLATION_EMAIL: { bgColor: "bg-blue-400", iconColor: "text-blue-500" },
  BOOKING_CONFIRMATION_EMAIL: { bgColor: "bg-pink-500", iconColor: "text-pink-500" },
  APP: { bgColor: "bg-green-500", iconColor: "text-green-500" },
  INVITATION_EMAIL: { bgColor: "bg-teal-500", iconColor: "text-teal-500" },
}

const ManageNotifications = () => { 
  const { event: eventId } = useParams()
  const { user, isLoadingCurrentEvent } = useContext(UserContext)
  const { setModal } = useContext(ModalContext)
  const [actionMenuLoadingIds, setActionMenuLoadingIds] = useState([])
  const [isLoadingWizard, setIsLoadingWizard] = useState(true)
  const [notificationRecipientTypes, setNotificationRecipientTypes] = useState(null)
  const [notificationStatus, setNotificationStatus] = useState(null)
  const [notificationTypes, setNotificationTypes] = useState(null)
  const [outstandingDeliveryBatchIds, setOutstandingDeliveryBatchIds] = useState(null)
  const [sesEmailStatuses, setSesEmailStatuses] = useState(null)
  const { setContextualSidebar } = useContext(ContextualSidebarContext)
  const [emailPreviewModal, setEmailPreviewModal] = useState({ show: false, html: null })
  const { notify } = useContext(NotificationContext)
  
  useSetCurrentEvent(eventId)

  const {data, loading, refetch} = useQuery(GET_NOTIFICATIONS, {
    fetchPolicy: "cache-and-network",
    variables: { id: eventId },
    notifyOnNetworkStatusChange : true,
    pollInterval: 900000,

    onError: (errors) => {
      notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while fetching notifications."), open: true })
      setIsLoadingWizard(false)
    }
  })

  useQuery(GET_SES_EMAIL_BATCHES, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    skip: outstandingDeliveryBatchIds !== null && outstandingDeliveryBatchIds.length === 0,
    pollInterval: 15000, // 15 seconds
    variables: { 
      input: { 
        event_id: eventId, 
        outstanding_only: true,
        types: ["INVITATION", "BOOKING_CANCELLATION","BOOKING_CONFIRMATION", "NOTIFICATION"]
      } 
    },

    onCompleted: (data) => {
      const newOutstandingDeliveryBatchIds = data?.sesEmailBatches.map(batch => batch.id) || []
      const newlyProcessedBatchIds = outstandingDeliveryBatchIds?.filter(id => !newOutstandingDeliveryBatchIds.includes(id)) || []
      setOutstandingDeliveryBatchIds(newOutstandingDeliveryBatchIds)      

      if (newlyProcessedBatchIds.length > 0) {
        refetch()
      }
    }
  })
    
  const [updateNotification] = useMutation(UPDATE_NOTIFICATION_MUTATION, {
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while updating the notification."), open: true })
  })

  const [previewInvitationEmail] = useLazyQuery(GET_PREVIEW_INVITATION_EMAIL, {
    variables: { input: { event_id: eventId } },
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while fetching the invitation email preview."), open: true }),
    onCompleted: (data) => setEmailPreviewModal({ show: true, html: data.previewInvitationEmail.html })
  })

  const [previewNotificationEmail] = useLazyQuery(GET_PREVIEW_NOTIFICATION_EMAIL, {
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while fetching the notification email preview."), open: true }),
    onCompleted: (data) => setEmailPreviewModal({ show: true, html: data.previewNotificationEmail.html })
  })

  const [previewBookingConfirmationEmail] = useLazyQuery(GET_PREVIEW_BOOKING_CONFIRMATION_EMAIL, {
    variables: { input: { event_id: eventId } },
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while fetching the booking confirmation email preview."), open: true }),
    onCompleted: (data) => setEmailPreviewModal({ show: true, html: data.previewBookingConfirmationEmail.html })
  })

  useQuery(GET_NOTIFICATION_META_DATA, {
    fetchPolicy: "cache-first",
    onCompleted: (data) => {
      setSesEmailStatuses(data.sesEmailStatuses)
      setNotificationTypes(data.notificationTypes)
      setNotificationRecipientTypes(data.notificationRecipientTypes)
      setNotificationStatus(data.notificationStatus)
      setIsLoadingWizard(false)
    },

    onError: (errors) => {
      notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while fetching notification metadata."), open: true })
      setIsLoadingWizard(false)
    }
  })
  
  const handleNotificationCreated = (notification) => {
    if(notification.type.is_email && notification.sesEmailBatch) {
      setOutstandingDeliveryBatchIds(prevState => [...prevState, notification.sesEmailBatch.id])
    }

    return refetch()
  }

  const handleCloseNotificationsWizard = () => {
    setModal(prevState => ({ ...prevState, isOpen: false, title: "", content: null }))
  }
  
  const handlePreviewEmail = (notification) => {
    const { code } = notification.type;
    setActionMenuLoadingIds(prevState => [...prevState, notification.id])

    const actions = {
      INVITATION_EMAIL: () => previewInvitationEmail(),
      CUSTOM_EMAIL: () => previewNotificationEmail({ variables: { input: { notification_id: notification.id } } }),
      BOOKING_CONFIRMATION_EMAIL: () => previewBookingConfirmationEmail(),
    };

    const action = actions[code];

    if (action) {
      action().finally(() => setActionMenuLoadingIds(prevState => prevState.filter(id => id !== notification.id)))
    } else {
      notify({ title: "Error", type: "danger", content: "Unknown email type", open: true })
      setActionMenuLoadingIds(prevState => prevState.filter(id => id !== notification.id))
    }
  }

  const handleStartBookingConfirmationWizard = () => {
    const initialConfigurationOverrides = {
      booking_confirmations_outstanding_only: true,
      type: notificationTypes.find(type => type.code === "BOOKING_CONFIRMATION_EMAIL"),
    }

    handleShowNotificationWizard(initialConfigurationOverrides)
  }

  const handleShowNotificationWizard = (initialConfigurationOverrides = null) => {
    setModal(prevState => ({ 
      ...prevState, 
      isOpen: true, 
      title: "Create Notification", 
      content: <NotificationsWizard 
        event={user.currentEvent} 
        handleClose={() => handleCloseNotificationsWizard()}
        handleNotificationCreated={handleNotificationCreated}
        notificationStatus={notificationStatus}
        notificationTypes={notificationTypes}
        notificationRecipientTypes={notificationRecipientTypes}
        initialConfigurationOverrides={initialConfigurationOverrides}
      /> 
    }))
  }
  
  const handleUpdateNotification = (notification, active) => {
    updateNotification({
      variables: { 
        input: {
          id: notification.id,
          active
        }
      }
    })
  }

  const handleShowNotificationDetails = (notification) => {
    setContextualSidebar(prevState => ({
      ...prevState,
      open: true,
      title: notification.title,
      subTitle: "Notification Details",
      content: 
        <NotificationDetails 
          notification={notification} 
          handleUpdateNotification={(active) => handleUpdateNotification(notification, active)}
        />,
      size: "lg",
    }))
  }

  const handleViewDeliveryStats = (notification) => {
    setContextualSidebar(prevState => ({
      ...prevState,
      open: true,
      title: notification.title,
      subTitle: "Notification Delivery Details",
      content: <NotificationDeliveryStats notification={notification} event={user?.currentEvent} statuses={sesEmailStatuses} />,
      size: "lg",
    }))
  }

  const getDeliveryStatus = (notification) => {
    if (notification.type.is_app) {
      return <Chip color="success" className="text-center text-white min-w-28">{notification.no_of_recipients} / {notification.no_of_recipients}</Chip>
    }

    if (notification.type.is_email) {
      if (notification?.sesEmailBatch?.no_of_queued > 0) {
        return (
          <Chip 
            color="primary" 
            className="text-center text-white min-w-28"
            endContent={<Spinner size="sm" className="ml-1 mr-2" variant="dots" color="white" />}
          >
            Sending
          </Chip>
        )
      }

      if (notification?.sesEmailBatch?.no_of_queued === 0) {
        const failureRate = notification.sesEmailBatch.no_of_failed > 0 
          ? (notification.sesEmailBatch.no_of_failed / notification.sesEmailBatch.no_of_emails) * 100
          : 0
          
        const color = failureRate > 25 ? "danger" : failureRate > 5 ? "warning" : "success"

        return (
          <Chip color={color} className="text-center text-white min-w-28">
            {notification.sesEmailBatch.no_of_delivered} / {notification.sesEmailBatch.no_of_emails}
          </Chip>
        ) 
      }
    }

    return <Chip color="warning" className="text-center text-white min-w-28">Unknown</Chip>
  }

  const getStatus = (notification) => {
    if(!notification.type.is_app) {
      return "-"
    }

    return (
      <Chip classNames={{ 
        "base": `${notification.status.code === "CRITICAL" ? 'bg-red-500' : 'bg-blue-500'} min-w-[116px] text-center`,
        "content": "text-white"
      }}>
        {notification.status.name}
      </Chip>  
    )
  }

  const getTitle = (notification) => {
    return truncate(notification.title || "-", { length: 50 })
  }
  
  const getType = (notification) => {
    const bgColor = notificationTypeColors[notification.type.code]?.bgColor || "bg-gray-500"
    
    return (
      <Chip classNames={{
        "base": `text-center ${bgColor}`,
        "content": "text-white",
      }}>
        {notification.type.name}
      </Chip>
    )
  }

  const getMoreMenu = (notification) => {
    const items = [
      { id: 1, handleClick: () => handleShowNotificationDetails(notification), caption: "View Details" },
    ]

    if (notification.type.is_email && notification.type.code !== "BOOKING_CANCELLATION_EMAIL") {
      items.push({ id: 2, handleClick: () => handlePreviewEmail(notification), caption: "Preview Email" })
    }

    if (notification.type.is_email) {
      items.push({ id: 3, handleClick: () => handleViewDeliveryStats(notification), caption: "View Delivery Stats" })
    }

    return items
  }

  const getTypeIcon = (notification) => {
    const iconColor = notificationTypeColors[notification.type.code]?.iconColor || "bg-gray-500"

    if (notification.type.is_email) {
      return <EnvelopeIcon className={`w-8 h-8 ${iconColor}`} />
    }

    if (notification.type.is_app && notification.active) {
      return <BellAlertIcon className={`w-8 h-8 ${iconColor}`} />
    }

    if (notification.type.is_app && !notification.active) {
      return <BellSnoozeIcon className={`-ml-0.5 ${iconColor} w-9 h-9`} />
    }
  }

  const createNotificationButton = (isReadOnly, size, classes = "") => {
    return (
      <Button 
        onPress={() => handleShowNotificationWizard()} 
        isDisabled={isReadOnly || isLoadingWizard}
        size={size}
        color="success" 
        className={`font-semibold text-white shrink-0 ${classes}`}
      >
        Create Notification
      </Button>
    )
  }

  const { ends } = getEventDateRange(user.currentEvent.sessions)
  const isReadOnlyMode = hasExpired(ends)

  return (
    <EventViewsTracker eventId={eventId}>
      <Page>
        <EventPageHeader
          title="Manage Parent Communications"
          icon={<BellAlertIcon className="page-header-icon" />}
          event={user?.currentEvent}
          actions={<EventMenu event={user?.currentEvent} user={user} />}
        />

        <CheckList 
          isReadOnly={isReadOnlyMode} 
          noOfBookings={data?.event?.no_of_bookings || 0} 
          noOfBookingConfirmationsSent={data?.event?.no_of_booking_confirmations_sent || 0} 
          handleStartBookingConfirmationWizard={handleStartBookingConfirmationWizard}
        />

        <Card fullWidth className="shadow-lg min-h-80">
          <CardHeader className="flex items-center justify-between text-white bg-indigo-800">
            <div className="flex items-center">
              <BellIcon className="w-12 h-12 mr-2" />

              <div>
                <p className="text-lg font-semibold">Your Event Notifications</p>
                <p className="text-sm">You have sent {data?.event?.notifications?.length || 0} notifications</p>
              </div>
            </div>
            
            {createNotificationButton(isReadOnlyMode, "md")}
          </CardHeader>

          <CardBody>          
            <Table 
              classNames={{
                wrapper: "rounded shadow-none p-0 overflow-y-hidden",
              }}
              isStriped 
              aria-label="Your event notifications"
              >
              <TableHeader>
                <TableColumn></TableColumn>
                <TableColumn>Type</TableColumn>
                <TableColumn>Title</TableColumn>
                <TableColumn align="center">Alert Type</TableColumn>
                <TableColumn align="end">Qty</TableColumn>
                <TableColumn align="end">Delivery Status</TableColumn>
                <TableColumn>Created</TableColumn>
                <TableColumn>Actions</TableColumn>
              </TableHeader>

              <TableBody>
                {data?.event?.notifications?.length > 0 && !loading && data.event.notifications.map((notification) => (
                  <TableRow key={notification.id}>
                    <TableCell>{getTypeIcon(notification)}</TableCell>
                    <TableCell>{getType(notification)}</TableCell>
                    <TableCell>{getTitle(notification)}</TableCell>
                    <TableCell>{getStatus(notification)}</TableCell>
                    <TableCell>{notification.no_of_recipients}</TableCell>
                    <TableCell align="end">{getDeliveryStatus(notification)}</TableCell>
                    <TableCell>{mySqlToLocal(notification.created_at).format('DD/MM/YY HH:mm')}</TableCell>
                    <TableCell>
                      <EllipsisDropdown 
                        items={getMoreMenu(notification)} 
                        isLoading={actionMenuLoadingIds.includes(notification.id)} 
                      />
                    </TableCell>
                  </TableRow>
                ))}    
              </TableBody>
            </Table>

            {(loading || isLoadingCurrentEvent) && <LoadingPlaceholder classNames="mt-2" caption="Please wait. Loading data..." icon={CircleStackIcon} />}

            {!loading && !isLoadingCurrentEvent && data?.event?.notifications?.length === 0 && (
              <EmptyPlaceholder classNames="mt-2" title="No Communications Found" description="No communications have been created yet." icon={BellSlashIcon}>
                {createNotificationButton(isReadOnlyMode, "lg", "mt-3")}
              </EmptyPlaceholder>
            )}
          </CardBody>
        </Card>

        {emailPreviewModal?.show && (
          <EmailPreviewModal 
            handleClose={() => setEmailPreviewModal(prev => ({ ...prev, show: false }))}
            html={emailPreviewModal?.html} 
            show={emailPreviewModal?.show}
            title="Notification Email Preview"
          />
        )}
      </Page>
    </EventViewsTracker>
  )
}

export default ManageNotifications